import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Building2, BellPlus, Info } from "lucide-react";

import { normalizeOrganizations, normalizeOrganization, orgHref, orgInitial, orgContext, locationsText, locationLine, contactText, orgSourceText, followText } from "@/lib/v3Orgs";
import { useCore } from "../useCore";
import { ConnectionError } from "../components/ConnectionError";
import { Loading } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { ResultTable, ResultRow, AreaCard, ResponsibleCard, SectionTitle, V3_CARD, V3_GHOST } from "../components/v3app";
import { InfoBox, SegmentTabs, StagePill } from "../components/v3people";
import { Blocked } from "../components/v3plans";

function organizationList(d) {
  const list = normalizeOrganizations(d);
  if (!list || list.length !== d.organizations.length || new Set(list.map((o) => o.id)).size !== list.length) throw new Error("Unreadable organization list");
  return { list };
}

function organizationDetail(d, requestedId) {
  const raw = d?.organization;
  const availability = raw?.availability;
  if (availability !== undefined && (!availability || typeof availability !== "object" || Array.isArray(availability) ||
      Object.values(availability).some((value) => typeof value !== "boolean"))) throw new Error("Unreadable organization availability");
  const unavailable = Object.fromEntries(["locations", "contact", "image", "following"].map((key) => [key, availability?.[key] === false]));
  const visible = { ...raw };
  for (const key of Object.keys(unavailable)) if (unavailable[key]) delete visible[key];
  const org = normalizeOrganization({ organization: visible });
  if (!org || org.id !== String(requestedId) ||
      (raw.offers !== undefined && (!Array.isArray(raw.offers) || org.offers?.length !== raw.offers.length))) throw new Error("Unreadable organization profile");
  return { org: { ...org, unavailable } };
}
const NOT_PUBLIC = "Öffentliche Angaben werden noch nicht bereitgestellt.";
const NO_CORE = "Der gemeinsame Core stellt noch keine Organisationsprofile bereit. Es werden keine Beispiele angezeigt.";

// M101: GET /member/organizations (D-100); without the endpoint only the honest notice and the general rule are shown.
export function Organizations() {
  const res = useCore("/member/organizations", organizationList);
  const list = res.data ? res.data.list : null;
  const missing = res.error === "notfound";
  return (
    <div data-m="M101" data-testid="orgs-page">
      <V3Title title="Organisationen entdecken" intro="Entdecke Organisationen und ihre öffentlichen Angebote." testid="orgs-title" />
      {res.error && !missing ? <ConnectionError onRetry={res.reload} /> : !res.data && !missing ? <Loading /> : (<>
        {missing && <StatusBanner tone="sand" icon={Building2} title="Organisationen noch nicht verfügbar" text={NO_CORE} testid="orgs-unavailable" />}
        <ResultTable testid="orgs-table" label="Organisationen">
          {(list || []).map((o) => <ResultRow key={o.id} initial={orgInitial(o)} title={o.name} context={orgContext(o)} to={orgHref(o)} testid={`org-${o.id}`} />)}
          {list && !list.length && <ResultRow initial="–" title="Noch keine Organisationen" context="Sobald Organisationen öffentliche Angebote zeigen, findest du sie hier." action="" testid="orgs-empty" />}
          <ResultRow initial="B" title="Bestätigung" context="Organisationsangaben sind keine Zugangszertifizierung" action="" testid="orgs-notice" />
        </ResultTable>
        <V3Actions className="mt-[20px]">
          {list && list[0] ? <Link to={orgHref(list[0])} data-testid="orgs-open-first" className={V3_PRIMARY}>Organisation ansehen<span className="sr-only">: {list[0].name}</span></Link>
            : <Link to="/suche" data-testid="orgs-back" className={V3_SECONDARY}>Zur Suche</Link>}
        </V3Actions>
      </>)}
    </div>
  );
}

function OrgMissing({ incomplete }) {
  return (<>
    <StatusBanner tone="sand" icon={Building2} title="Organisation nicht verfügbar" testid="org-missing"
      text={incomplete ? "Die Antwort des gemeinsamen Core war unvollständig. Es wird nichts angezeigt." : "Diese Organisation ist nicht öffentlich sichtbar, oder Organisationsprofile sind im gemeinsamen Core noch nicht eingerichtet."} />
    <V3Actions><Link to="/organisationen" data-testid="org-missing-back" className={V3_PRIMARY}>Zur Übersicht</Link></V3Actions>
  </>);
}

// Shared loader of M102/M103: GET /member/organizations/{id} (D-101); 404/410 or a broken shape never shows invented details.
function WithOrganization({ m, title, intro, testid, children }) {
  const { id } = useParams();
  const res = useCore(`/member/organizations/${encodeURIComponent(id)}`, (d) => organizationDetail(d, id));
  const org = res.data ? res.data.org : null;
  const missing = res.error === "notfound" || res.error === "gone";
  return (
    <div data-m={m} data-testid={testid}>
      <V3Title title={title} intro={intro} testid={`${testid}-title`} />
      {res.error && !missing ? <ConnectionError onRetry={res.reload} /> : !res.data && !missing ? <Loading /> : missing ? <OrgMissing incomplete={Boolean(res.data)} /> : children(org)}
    </div>
  );
}

function OrgHead({ org }) {
  return (
    <div className={`${V3_CARD} mb-[20px] grid gap-[18px] p-[10px] pb-[22px] lg:grid-cols-[143px_minmax(0,1fr)] lg:gap-[19px] lg:pb-[10px]`} data-testid="org-head">
      {org.image ? <img src={org.image.url} alt={org.image.alt} className="h-[150px] w-full rounded-[10px] object-cover lg:h-[152px]" data-testid="org-image" />
        : <div aria-hidden="true" data-testid="org-image-none" className="flex h-[150px] flex-col items-center justify-center gap-[8px] rounded-[10px] bg-v3-lilac text-v3-magenta lg:h-[152px]">
            <Building2 className="h-[30px] w-[30px]" strokeWidth={1.6} /><span className="text-[12.5px] font-bold text-v3-muted">{org.unavailable.image ? "Öffentliches Bild noch nicht bereitgestellt" : "Kein Bild angegeben"}</span></div>}
      <div className="min-w-0 px-[6px] lg:px-0 lg:pt-[14px]">
        <h2 className="break-words text-[23px] font-bold leading-[31px] text-v3-ink lg:text-[26px] lg:leading-[36px]" data-testid="org-name">{org.name}</h2>
        <p className="mt-[12px] break-words text-[14px] leading-[21px] text-v3-muted lg:mt-[26px]" data-testid="org-description">{org.description || "Keine Beschreibung angegeben"}</p>
      </div>
    </div>
  );
}

const TABS = [["profil", "Profil"], ["standorte", "Standorte"], ["kontakt", "Kontakt"]];

function TabPanel({ tab, org }) {
  if (tab === "standorte" && org.unavailable.locations) return <DetailRow label="Standorte" value={NOT_PUBLIC} testid="org-locations-unavailable" />;
  if (tab === "standorte") return org.locations.length
    ? org.locations.map((l, i) => <DetailRow key={i} label={`Standort ${i + 1}`} value={locationLine(l)} testid={`org-location-${i}`} />)
    : <DetailRow label="Standorte" value="Keine Standorte angegeben" testid="org-locations-none" />;
  if (tab === "kontakt" && org.unavailable.contact) return <DetailRow label="Kontakt" value={NOT_PUBLIC} testid="org-contact-unavailable" />;
  if (tab === "kontakt") return (<>
    <DetailRow label="Nachricht über VISIBILIS" value="Noch nicht verfügbar" testid="org-contact-message" />
    <DetailRow label="E-Mail" value={org.contact.email || "Nicht angegeben"} testid="org-contact-email" />
    <DetailRow label="Telefon" value={org.contact.phone || "Nicht angegeben"} testid="org-contact-phone" />
  </>);
  return (<>
    <DetailRow label="Organisation" value={org.name} testid="org-row-name" />
    <DetailRow label="Angebot" value={org.offer || "Nicht angegeben"} testid="org-row-offer" />
    <DetailRow label="Standorte" value={org.unavailable.locations ? NOT_PUBLIC : locationsText(org)} testid="org-row-locations" />
    <DetailRow label="Kontakt" value={org.unavailable.contact ? NOT_PUBLIC : contactText(org)} testid="org-row-contact" />
  </>);
}

// Offers of this organisation, only from the proposed `offers[]` field (D-104); links only to activities with an id.
function Offers({ org }) {
  return (
    <section id="org-offers" aria-labelledby="org-offers-title" className="mt-[26px]" data-testid="org-offers">
      <SectionTitle id="org-offers-title">Öffentliche Angebote</SectionTitle>
      <div className="mt-[14px]">
        {org.offers === null ? <Blocked id="org-offers-unavailable">Angebote einer Organisation liefert der gemeinsame Core noch nicht. Alle sichtbaren Aktivitäten findest du unter „Aktivitäten“.</Blocked>
          : org.offers.length ? <DetailList>{org.offers.map((x) => <DetailRow key={x.id} label={x.title} value={x.activityId ? "Aktivität ansehen" : "Nur als Beschreibung angegeben"}
              to={x.activityId ? `/aktivitaeten/${encodeURIComponent(x.activityId)}` : undefined} testid={`org-offer-${x.id}`} />)}</DetailList>
          : <p className="text-[14px] text-v3-muted" data-testid="org-offers-empty">Keine öffentlichen Angebote angegeben.</p>}
      </div>
    </section>
  );
}

function OrgProfile({ org }) {
  const [tab, setTab] = useState("profil");
  const [showOffers, setShowOffers] = useState(false);
  return (
    <V3Columns layout="detail" aside={<><AreaCard text={org.name} testid="org-area" /><ResponsibleCard /></>} main={<>
      <OrgHead org={org} />
      <SegmentTabs items={TABS} value={tab} onChange={setTab} label="Bereiche der Organisation" testid="org-tabs" />
      <div role="tabpanel" aria-label={TABS.find(([k]) => k === tab)[1]} className="mt-[17px] space-y-[11px]" data-testid={`org-panel-${tab}`}><TabPanel tab={tab} org={org} /></div>
      <div className="mt-[11px]"><InfoBox icon={Info} title="Quelle und Zuständigkeit" text={orgSourceText(org)} testid="org-source" /></div>
      <V3Actions className="mt-[20px]">
        <button type="button" onClick={() => setShowOffers((v) => !v)} aria-expanded={showOffers} aria-controls="org-offers" data-testid="org-offers-toggle" className={V3_PRIMARY}>Angebote entdecken</button>
        <Link to="/organisationen" data-testid="org-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
      <div className="mt-[16px]"><Link to={orgHref(org, "/folgen")} data-testid="org-follow-link" className={V3_GHOST}><BellPlus className="h-4 w-4" aria-hidden="true" />Organisation folgen</Link></div>
      {showOffers && <Offers org={org} />}
    </>} />
  );
}

export const OrganizationProfile = () => (
  <WithOrganization m="M102" title="Profil einer Organisation" intro="Hier findest du öffentliche Informationen und Angebote der Organisation." testid="org-page">{(org) => <OrgProfile org={org} />}</WithOrganization>
);

// M103: following needs POST/DELETE /member/organizations/{id}/follow (D-102), so the action stays locked and nothing is sent.
function FollowForm({ org }) {
  return (
    <V3Columns layout="detail" aside={<><AreaCard title="Ausgewählter Vorgang" text={org.name} testid="follow-area" /><ResponsibleCard /></>} main={<>
      <StagePill testid="follow-stage">Vor der Entscheidung</StagePill>
      <DetailList>
        <DetailRow label="Organisation" value={org.name} testid="follow-org" />
        <DetailRow label="Folgen" value="Neue sichtbare Angebote leichter finden" testid="follow-effect" />
        <DetailRow label="Mitgliedschaft" value="Entsteht dadurch nicht" testid="follow-membership" />
        <DetailRow label="Persönliche Freigaben" value="Bleiben unverändert" testid="follow-grants" />
        <DetailRow label="Status" value={followText(org.following)} testid="follow-status" />
      </DetailList>
      <Blocked id="follow-blocked">Folgen ist im gemeinsamen Core noch nicht eingerichtet. Es wird nichts gesendet und nichts gespeichert; dein Status bleibt unverändert.</Blocked>
      <V3Actions>
        <button type="button" disabled aria-describedby="follow-blocked" data-testid="follow-submit" className={V3_PRIMARY}>{org.following === true ? "Nicht mehr folgen" : "Organisation folgen"}</button>
        <Link to={orgHref(org)} data-testid="follow-cancel" className={V3_SECONDARY}>Abbrechen</Link>
      </V3Actions>
    </>} />
  );
}

export const OrganizationFollow = () => (
  <WithOrganization m="M103" title="Einem Anbieter folgen" intro="Folge den öffentlichen Angeboten dieser Organisation." testid="follow-page">{(org) => <FollowForm org={org} />}</WithOrganization>
);
